import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import baseUrl from '../../../config/baseURL';
import { getCurentOrgDataFromStorage } from '../../../services/organization';
import { consumeInstagramOnboardingState } from '../utils/onboardingState';

type Status = 'loading' | 'success' | 'error';

const InstagramDirectLogInSuccessPage: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('Finishing Instagram connection...');
  const [orgId, setOrgId] = useState<string>('');
  const toast = useToast();

  useEffect(() => {
    const finalize = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const error = params.get('error');
      const state = params.get('state');

      const orgResponse = await getCurentOrgDataFromStorage();
      const currentOrgId =
        orgResponse.success &&
        orgResponse.data &&
        typeof orgResponse.data === 'object'
          ? orgResponse.data.id
          : '';

      setOrgId(currentOrgId || '');

      if (!currentOrgId) {
        setStatus('error');
        setMessage('Organization context is missing. Start setup again.');
        return;
      }

      const stateValidation = consumeInstagramOnboardingState(state, currentOrgId);
      if (!stateValidation.valid) {
        setStatus('error');
        setMessage('Instagram setup could not be verified. Start setup again.');
        return;
      }
      if (stateValidation.weakValidation) {
        console.warn(
          'Instagram onboarding state was recovered without original session storage.',
        );
      }

      if (error) {
        setStatus('error');
        setMessage('Instagram access was denied or interrupted.');
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('The callback did not include an authorization code.');
        return;
      }

      try {
        const response = await fetch(
          `${baseUrl}/core/instagram/verification/instagram-direct-callback`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              org_id: currentOrgId,
              code,
              state,
            }),
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.error ||
              data?.details?.error_message ||
              data?.details?.message ||
              'Unable to verify Instagram connection',
          );
        }

        setStatus('success');
        setMessage(
          data?.username
            ? `Instagram account @${data.username} is now connected.`
            : 'Instagram account is now connected.',
        );
        toast({
          title: 'Instagram connected',
          description: 'Your inbox is ready to use.',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
      } catch (finalizeError: any) {
        setStatus('error');
        setMessage(finalizeError?.message || 'Connection could not be completed.');
      }
    };

    finalize();
  }, []);

  return (
    <Flex minH="100vh" align="center" justify="center" bg="#F7F4F8" px={4}>
      <Box
        w="full"
        maxW="640px"
        bg="white"
        borderRadius="32px"
        border="1px solid"
        borderColor="rgba(15, 23, 42, 0.08)"
        boxShadow="0 24px 64px rgba(15,23,42,0.08)"
        p={{ base: 7, md: 10 }}
      >
        <VStack spacing={6} align="stretch">
          <Badge colorScheme="pink" borderRadius="full" px={3} py={1} w="fit-content">
            Instagram setup
          </Badge>

          {status === 'loading' ? (
            <VStack spacing={5} align="center" py={8}>
              <Spinner color="#C13584" size="xl" />
              <VStack spacing={1}>
                <Heading size="lg" color="gray.900">
                  Completing connection
                </Heading>
                <Text color="gray.500">{message}</Text>
              </VStack>
            </VStack>
          ) : null}

          {status === 'success' ? (
            <VStack spacing={5} align="stretch">
              <Heading size="lg" color="gray.900">
                Instagram connected
              </Heading>
              <Text color="gray.500" lineHeight="1.8">
                {message} You can continue into the inbox and start managing
                conversations from the connected workspace.
              </Text>
              <Button
                colorScheme="pink"
                borderRadius="full"
                onClick={() =>
                  (window.location.href = `/organization/${orgId}/inbox/instagram/conversations`)
                }
              >
                Open Instagram Inbox
              </Button>
            </VStack>
          ) : null}

          {status === 'error' ? (
            <VStack spacing={5} align="stretch">
              <Heading size="lg" color="gray.900">
                Connection not completed
              </Heading>
              <Text color="gray.500" lineHeight="1.8">
                {message}
              </Text>
              <Button
                colorScheme="pink"
                variant="outline"
                borderRadius="full"
                onClick={() => (window.location.href = '/organization/inbox/instagram')}
              >
                Try setup again
              </Button>
            </VStack>
          ) : null}
        </VStack>
      </Box>
    </Flex>
  );
};

export default InstagramDirectLogInSuccessPage;
